import type { FeedItem } from '@knocklabs/client';
import type { GenericData } from '@knocklabs/types/src/common';

import notifications from './notification-types';
import type {
  GetItem,
  NotificationLookupData,
} from './notification-types/types';

export type NotificationItem = {
  url: null | string;
  message: null | string;
  feedItem: FeedItem<GenericData>;
  id: null | string;
};

export type KnockRenderedContent = { message: string; url: null | string };

/** Which source rendered a feed item's in-app content. */
export type RenderPath = 'knock' | 'fallback' | 'dropped';

/**
 * Telemetry-only view of how a feed item resolved, derived from the SAME decision the
 * renderer makes (`resolveRenderPath`) so the metric can never drift from real behaviour.
 * Carries no message text or entity data — only the bounded dimensions the cutover
 * dashboard needs (RSP-6347). `insertedAt` is raw; the age bucket is computed at emit time.
 */
export type RenderSignal = {
  id: string;
  renderPath: RenderPath;
  workflowKey: string | null;
  knockHasLink: boolean;
  handlerExists: boolean;
  insertedAt: string | null;
};

type Block = FeedItem<GenericData>['blocks'][number];

const isTextBlock = (
  block: Block
): block is Extract<Block, { type: 'markdown' | 'text' }> =>
  block.type === 'markdown' || block.type === 'text';

const isButtonSetBlock = (
  block: Block
): block is Extract<Block, { type: 'button_set' }> =>
  block.type === 'button_set';

/**
 * Read Knock-rendered in-app content off a feed item.
 *
 * The message is taken from the first markdown/text block's plain `content` — never the
 * `rendered` HTML, so entity-title-derived markup can't be injected into the cell (see
 * ENG-184). The deep link is taken from the first button_set button's `action`.
 *
 * Returns `null` when Knock rendered no usable text — the in-app templates are empty until
 * each workflow's content is migrated into Knock, so callers fall back to the legacy
 * per-workflow handler.
 */
export const extractKnockContent = (
  feedItem: FeedItem<GenericData>
): KnockRenderedContent | null => {
  const blocks = feedItem.blocks ?? [];

  const message = blocks.find(isTextBlock)?.content?.trim() ?? '';
  if (!message) {
    return null;
  }

  const action = blocks.find(isButtonSetBlock)?.buttons[0]?.action;

  return { message, url: action || null };
};

/**
 * The single render decision, shared by `buildNotificationItem` (what to show) and
 * `buildRenderSignal` (what to report). Centralising it here is what guarantees the cutover
 * telemetry (RSP-6347) measures exactly what the user sees.
 *
 * Knock content is used when it carries a message AND either a deep link was rendered or
 * there is no legacy handler for this workflow (in which case Knock content is strictly
 * better than the nothing rendered today). A handler-backed item whose Knock content lacks
 * a link falls back to the handler so navigation is preserved. `dropped` when neither path
 * can render — preserving the prior behaviour of dropping items with no handler.
 */
export const resolveRenderPath = (
  knock: KnockRenderedContent | null,
  handler: GetItem | undefined
): RenderPath => {
  if (knock && (knock.url !== null || !handler)) {
    return 'knock';
  }

  if (handler) {
    return 'fallback';
  }

  return 'dropped';
};

const classifyFeedItem = (
  feedItem: FeedItem<GenericData>
): {
  handler: GetItem | undefined;
  knock: KnockRenderedContent | null;
  path: RenderPath;
} => {
  // `source` is typed non-optional but Knock's real-time socket can deliver partial
  // items; guard so one malformed message drops out rather than throwing in the map.
  const handler = feedItem.source?.key
    ? notifications[feedItem.source.key]
    : undefined;
  const knock = extractKnockContent(feedItem);

  return { handler, knock, path: resolveRenderPath(knock, handler) };
};

/**
 * Build the rendered `NotificationItem` for a feed item, preferring Knock-templated
 * content over the legacy app-side handler (see `resolveRenderPath`). Returns `null` when
 * neither path can render.
 */
export const buildNotificationItem = (
  feedItem: FeedItem<GenericData>,
  lookupData: NotificationLookupData
): NotificationItem | null => {
  const { handler, knock, path } = classifyFeedItem(feedItem);

  if (path === 'knock' && knock) {
    return { message: knock.message, url: knock.url, id: null, feedItem };
  }

  if (path === 'fallback' && handler) {
    const details = handler(feedItem, lookupData);

    return {
      url: details.url,
      message: details.message,
      id: details.id,
      feedItem,
    };
  }

  return null;
};

/**
 * Derive the cutover-telemetry signal for a feed item — the same classification the
 * renderer uses, minus any message/entity content. Consumed by
 * `useNotificationRenderTelemetry` to count Knock-rendered vs app-hydration (fallback)
 * renders while the migration runs (RSP-6347); removed alongside the fallback machinery in
 * Stage 6 (RSP-6235).
 */
export const buildRenderSignal = (
  feedItem: FeedItem<GenericData>
): RenderSignal => {
  const { handler, knock, path } = classifyFeedItem(feedItem);

  return {
    id: feedItem.id,
    renderPath: path,
    workflowKey: feedItem.source?.key ?? null,
    knockHasLink: knock?.url != null,
    handlerExists: handler !== undefined,
    insertedAt: feedItem.inserted_at ?? null,
  };
};
